/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * BulkCreateDialog - Premium dialog for bulk creating internal tasks
 *
 * A refined, luxury-grade interface for configuring bulk task creation
 * with elegant form controls and sophisticated visual hierarchy.
 */

import { Check, ChevronDown, Layers, Sparkles } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useBulkCreate } from '@/contexts/BulkCreateContext';
import { cn } from '@/lib/utils';
import {
  type AssigneeRole,
  type ExternalTaskBulkCreateConflictMode,
} from '@/services/api/generated/externalTaskService';
import { tasksApiClient } from '@/services/api/tasksService';
import type { TaskGroup, TaskType } from '@/services/types/client-intake-types';

interface BulkCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  externalTaskIds: string[];
  onSuccess?: (operationId: string) => void;
}

const ASSIGNEE_ROLES: { value: AssigneeRole; label: string }[] = [
  { value: 'managing_paralegal', label: 'Managing Paralegal' },
  { value: 'police_report_manager', label: 'Police Report Manager' },
  { value: 'medical_records_manager', label: 'Medical Records Manager' },
  { value: 'demands_team', label: 'Demands Team' },
  { value: 'benefits_specialist', label: 'Benefits Specialist' },
  { value: 'claims_team', label: 'Claims Team' },
  { value: 'acknowledgement_team', label: 'Acknowledgement Team' },
  { value: 'treatment_team', label: 'Treatment Team' },
];

// Premium select button styles
const selectButtonStyles = cn(
  'group relative flex w-full items-center justify-between',
  'rounded-xl border px-4 py-3 text-left text-sm',
  'bg-gradient-to-b from-white to-zinc-50/80',
  'dark:from-zinc-900 dark:to-zinc-900/80',
  'border-zinc-200/80 dark:border-zinc-700/80',
  'shadow-sm shadow-zinc-900/5 dark:shadow-zinc-900/20',
  'transition-all duration-200 ease-out',
  'hover:border-zinc-300 hover:shadow-md hover:shadow-zinc-900/10',
  'dark:hover:border-zinc-600 dark:hover:shadow-zinc-900/30',
  'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
  'dark:focus:ring-blue-400/20 dark:focus:border-blue-400',
);

const selectButtonDisabledStyles = cn(
  'opacity-40 cursor-not-allowed',
  'hover:border-zinc-200/80 hover:shadow-sm',
  'dark:hover:border-zinc-700/80',
);

const labelStyles = cn(
  'block text-xs font-semibold uppercase tracking-wider',
  'text-zinc-500 dark:text-zinc-400',
  'mb-2',
);

export const BulkCreateDialog: React.FC<BulkCreateDialogProps> = ({
  isOpen,
  onClose,
  externalTaskIds,
  onSuccess,
}) => {
  const { data: taskTypes = [], isLoading: isLoadingTypes } =
    tasksApiClient.useGetTaskTypesQuery();
  const { data: taskGroups = [], isLoading: isLoadingGroups } =
    tasksApiClient.useGetTaskGroupsQuery();

  const { startOperation, isStarting } = useBulkCreate();

  const [selectedTaskGroup, setSelectedTaskGroup] =
    React.useState<TaskGroup | null>(null);
  const [selectedTaskType, setSelectedTaskType] =
    React.useState<TaskType | null>(null);
  const [selectedRole, setSelectedRole] = React.useState<
    AssigneeRole | undefined
  >(undefined);
  const [conflictMode, setConflictMode] =
    React.useState<ExternalTaskBulkCreateConflictMode>('SKIP');

  const [taskGroupOpen, setTaskGroupOpen] = React.useState(false);
  const [taskTypeOpen, setTaskTypeOpen] = React.useState(false);
  const [roleOpen, setRoleOpen] = React.useState(false);
  const [conflictModeOpen, setConflictModeOpen] = React.useState(false);

  const filteredTaskTypes = React.useMemo(() => {
    if (!selectedTaskGroup) return [];
    return taskTypes.filter((t) => t.group === selectedTaskGroup.id);
  }, [taskTypes, selectedTaskGroup]);

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedTaskGroup(null);
      setSelectedTaskType(null);
      setSelectedRole(undefined);
      setConflictMode('SKIP');
    }
  }, [isOpen]);

  React.useEffect(() => {
    setSelectedTaskType(null);
  }, [selectedTaskGroup]);

  const handleSubmit = React.useCallback(async () => {
    if (!selectedTaskType) return;

    try {
      const operationId = await startOperation({
        external_task_ids: externalTaskIds,
        task_type_id: selectedTaskType.id,
        assignee_role: selectedRole,
        conflict_mode: conflictMode,
      });

      onClose();
      onSuccess?.(operationId);
    } catch (error) {
      console.error('Failed to start bulk create operation:', error);
    }
  }, [
    startOperation,
    externalTaskIds,
    selectedTaskType,
    selectedRole,
    conflictMode,
    onClose,
    onSuccess,
  ]);

  const isLoading = isLoadingTypes || isLoadingGroups;
  const canSubmit =
    selectedTaskType !== null &&
    selectedRole !== null &&
    !isStarting &&
    !isLoading;

  // Calculate completion for visual feedback
  const completedSteps = [
    selectedTaskGroup,
    selectedTaskType,
    selectedRole,
  ].filter(Boolean).length;

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      {/* Header with refined typography */}
      <div className="relative px-6 pt-6 pb-4">
        {/* Subtle gradient accent */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zinc-200 to-transparent dark:via-zinc-700" />

        <div className="flex items-start justify-between">
          <div>
            <DialogTitle className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              Create Internal Tasks
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              <span className="inline-flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5" />
                <span className="font-medium text-zinc-700 dark:text-zinc-300">
                  {externalTaskIds.length.toLocaleString()}
                </span>{' '}
                task{externalTaskIds.length !== 1 ? 's' : ''} selected
              </span>
            </DialogDescription>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center gap-1.5">
            {[0, 1, 2].map((step) => (
              <div
                key={step}
                className={cn(
                  'h-1.5 w-6 rounded-full transition-all duration-300',
                  step < completedSteps
                    ? 'bg-emerald-500 dark:bg-emerald-400'
                    : 'bg-zinc-200 dark:bg-zinc-700',
                )}
              />
            ))}
          </div>
        </div>
      </div>

      <DialogBody className="px-6 py-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="relative">
              <div className="h-10 w-10 rounded-full border-2 border-zinc-200 dark:border-zinc-700" />
              <div className="absolute inset-0 h-10 w-10 animate-spin rounded-full border-2 border-transparent border-t-blue-500" />
            </div>
            <p className="mt-4 text-sm text-zinc-500">
              Loading configuration...
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Task Configuration Section */}
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Task Group */}
              <div>
                <label htmlFor="task-group-select" className={labelStyles}>
                  Task Group
                </label>
                <Popover open={taskGroupOpen} onOpenChange={setTaskGroupOpen}>
                  <PopoverTrigger asChild>
                    <button
                      id="task-group-select"
                      type="button"
                      role="combobox"
                      aria-expanded={taskGroupOpen}
                      aria-controls="task-group-listbox"
                      className={selectButtonStyles}
                    >
                      <span
                        className={cn(
                          'truncate font-medium',
                          selectedTaskGroup
                            ? 'text-zinc-900 dark:text-zinc-50'
                            : 'text-zinc-400 dark:text-zinc-500',
                        )}
                      >
                        {selectedTaskGroup?.name ?? 'Select group...'}
                      </span>
                      <ChevronDown
                        className={cn(
                          'ml-2 h-4 w-4 shrink-0 transition-transform duration-200',
                          'text-zinc-400 group-hover:text-zinc-600',
                          'dark:text-zinc-500 dark:group-hover:text-zinc-300',
                          taskGroupOpen && 'rotate-180',
                        )}
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0 overflow-hidden rounded-xl border-zinc-200/80 dark:border-zinc-700/80 shadow-xl"
                    align="start"
                  >
                    <Command>
                      <CommandInput
                        placeholder="Search groups..."
                        className="border-b border-zinc-100 dark:border-zinc-800"
                      />
                      <CommandList
                        id="task-group-listbox"
                        className="max-h-[240px]"
                      >
                        <CommandEmpty className="py-6 text-center text-sm text-zinc-500">
                          No groups found
                        </CommandEmpty>
                        <CommandGroup>
                          {taskGroups.map((group) => (
                            <CommandItem
                              key={group.id}
                              value={group.name}
                              onSelect={() => {
                                setSelectedTaskGroup(group);
                                setTaskGroupOpen(false);
                              }}
                              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
                            >
                              <div
                                className={cn(
                                  'flex h-4 w-4 items-center justify-center rounded-full border transition-all',
                                  selectedTaskGroup?.id === group.id
                                    ? 'border-emerald-500 bg-emerald-500 text-white'
                                    : 'border-zinc-300 dark:border-zinc-600',
                                )}
                              >
                                {selectedTaskGroup?.id === group.id && (
                                  <Check
                                    className="h-2.5 w-2.5"
                                    strokeWidth={3}
                                  />
                                )}
                              </div>
                              <span className="font-medium">{group.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Task Type */}
              <div>
                <label htmlFor="task-type-select" className={labelStyles}>
                  Task Type
                </label>
                <Popover open={taskTypeOpen} onOpenChange={setTaskTypeOpen}>
                  <PopoverTrigger asChild>
                    <button
                      id="task-type-select"
                      type="button"
                      role="combobox"
                      aria-expanded={taskTypeOpen}
                      aria-controls="task-type-listbox"
                      disabled={!selectedTaskGroup}
                      className={cn(
                        selectButtonStyles,
                        !selectedTaskGroup && selectButtonDisabledStyles,
                      )}
                    >
                      <span
                        className={cn(
                          'truncate font-medium',
                          selectedTaskType
                            ? 'text-zinc-900 dark:text-zinc-50'
                            : 'text-zinc-400 dark:text-zinc-500',
                        )}
                      >
                        {selectedTaskType?.name ??
                          (selectedTaskGroup
                            ? 'Select type...'
                            : 'Select group first')}
                      </span>
                      <ChevronDown
                        className={cn(
                          'ml-2 h-4 w-4 shrink-0 transition-transform duration-200',
                          'text-zinc-400 group-hover:text-zinc-600',
                          'dark:text-zinc-500 dark:group-hover:text-zinc-300',
                          taskTypeOpen && 'rotate-180',
                        )}
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0 overflow-hidden rounded-xl border-zinc-200/80 dark:border-zinc-700/80 shadow-xl"
                    align="start"
                  >
                    <Command>
                      <CommandInput
                        placeholder="Search types..."
                        className="border-b border-zinc-100 dark:border-zinc-800"
                      />
                      <CommandList
                        id="task-type-listbox"
                        className="max-h-[240px]"
                      >
                        <CommandEmpty className="py-6 text-center text-sm text-zinc-500">
                          No types found
                        </CommandEmpty>
                        <CommandGroup>
                          {filteredTaskTypes.map((taskType) => (
                            <CommandItem
                              key={taskType.id}
                              value={taskType.name}
                              onSelect={() => {
                                setSelectedTaskType(taskType);
                                setTaskTypeOpen(false);
                              }}
                              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
                            >
                              <div
                                className={cn(
                                  'flex h-4 w-4 items-center justify-center rounded-full border transition-all',
                                  selectedTaskType?.id === taskType.id
                                    ? 'border-emerald-500 bg-emerald-500 text-white'
                                    : 'border-zinc-300 dark:border-zinc-600',
                                )}
                              >
                                {selectedTaskType?.id === taskType.id && (
                                  <Check
                                    className="h-2.5 w-2.5"
                                    strokeWidth={3}
                                  />
                                )}
                              </div>
                              <span className="font-medium">
                                {taskType.name}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-200/60 dark:border-zinc-700/60" />
              </div>
            </div>

            {/* Assignment Section */}
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Assignee Role */}
              <div>
                <label htmlFor="assignee-role-select" className={labelStyles}>
                  Assignee Role
                </label>
                <Popover open={roleOpen} onOpenChange={setRoleOpen}>
                  <PopoverTrigger asChild>
                    <button
                      id="assignee-role-select"
                      type="button"
                      role="combobox"
                      aria-expanded={roleOpen}
                      aria-controls="assignee-role-listbox"
                      className={selectButtonStyles}
                    >
                      <span
                        className={cn(
                          'truncate font-medium',
                          selectedRole
                            ? 'text-zinc-900 dark:text-zinc-50'
                            : 'text-zinc-400 dark:text-zinc-500',
                        )}
                      >
                        {selectedRole
                          ? ASSIGNEE_ROLES.find((r) => r.value === selectedRole)
                              ?.label
                          : 'Select role...'}
                      </span>
                      <ChevronDown
                        className={cn(
                          'ml-2 h-4 w-4 shrink-0 transition-transform duration-200',
                          'text-zinc-400 group-hover:text-zinc-600',
                          'dark:text-zinc-500 dark:group-hover:text-zinc-300',
                          roleOpen && 'rotate-180',
                        )}
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0 overflow-hidden rounded-xl border-zinc-200/80 dark:border-zinc-700/80 shadow-xl"
                    align="start"
                  >
                    <Command>
                      <CommandInput
                        placeholder="Search roles..."
                        className="border-b border-zinc-100 dark:border-zinc-800"
                      />
                      <CommandList
                        id="assignee-role-listbox"
                        className="max-h-[240px]"
                      >
                        <CommandEmpty className="py-6 text-center text-sm text-zinc-500">
                          No roles found
                        </CommandEmpty>
                        <CommandGroup>
                          {ASSIGNEE_ROLES.map((role) => (
                            <CommandItem
                              key={role.value}
                              value={role.label}
                              onSelect={() => {
                                setSelectedRole(role.value);
                                setRoleOpen(false);
                              }}
                              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
                            >
                              <div
                                className={cn(
                                  'flex h-4 w-4 items-center justify-center rounded-full border transition-all',
                                  selectedRole === role.value
                                    ? 'border-emerald-500 bg-emerald-500 text-white'
                                    : 'border-zinc-300 dark:border-zinc-600',
                                )}
                              >
                                {selectedRole === role.value && (
                                  <Check
                                    className="h-2.5 w-2.5"
                                    strokeWidth={3}
                                  />
                                )}
                              </div>
                              <span className="font-medium">{role.label}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Conflict Mode */}
              <div>
                <label htmlFor="conflict-mode-select" className={labelStyles}>
                  If Already Linked
                </label>
                <Popover
                  open={conflictModeOpen}
                  onOpenChange={setConflictModeOpen}
                >
                  <PopoverTrigger asChild>
                    <button
                      id="conflict-mode-select"
                      type="button"
                      role="combobox"
                      aria-expanded={conflictModeOpen}
                      aria-controls="conflict-mode-listbox"
                      className={selectButtonStyles}
                    >
                      <span className="truncate font-medium text-zinc-900 dark:text-zinc-50">
                        {conflictMode === 'SKIP'
                          ? 'Skip existing'
                          : 'Create anyway'}
                      </span>
                      <ChevronDown
                        className={cn(
                          'ml-2 h-4 w-4 shrink-0 transition-transform duration-200',
                          'text-zinc-400 group-hover:text-zinc-600',
                          'dark:text-zinc-500 dark:group-hover:text-zinc-300',
                          conflictModeOpen && 'rotate-180',
                        )}
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0 overflow-hidden rounded-xl border-zinc-200/80 dark:border-zinc-700/80 shadow-xl"
                    align="start"
                  >
                    <Command>
                      <CommandList id="conflict-mode-listbox">
                        <CommandGroup>
                          <CommandItem
                            value="Skip existing"
                            onSelect={() => {
                              setConflictMode('SKIP');
                              setConflictModeOpen(false);
                            }}
                            className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
                          >
                            <div
                              className={cn(
                                'flex h-4 w-4 items-center justify-center rounded-full border transition-all',
                                conflictMode === 'SKIP'
                                  ? 'border-emerald-500 bg-emerald-500 text-white'
                                  : 'border-zinc-300 dark:border-zinc-600',
                              )}
                            >
                              {conflictMode === 'SKIP' && (
                                <Check
                                  className="h-2.5 w-2.5"
                                  strokeWidth={3}
                                />
                              )}
                            </div>
                            <span className="font-medium">Skip existing</span>
                          </CommandItem>
                          <CommandItem
                            value="Create anyway"
                            onSelect={() => {
                              setConflictMode('CREATE_NEW');
                              setConflictModeOpen(false);
                            }}
                            className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
                          >
                            <div
                              className={cn(
                                'flex h-4 w-4 items-center justify-center rounded-full border transition-all',
                                conflictMode === 'CREATE_NEW'
                                  ? 'border-emerald-500 bg-emerald-500 text-white'
                                  : 'border-zinc-300 dark:border-zinc-600',
                              )}
                            >
                              {conflictMode === 'CREATE_NEW' && (
                                <Check
                                  className="h-2.5 w-2.5"
                                  strokeWidth={3}
                                />
                              )}
                            </div>
                            <span className="font-medium">Create anyway</span>
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        )}
      </DialogBody>

      {/* Footer with refined actions */}
      <DialogActions className="px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
        <Button
          plain
          onClick={onClose}
          disabled={isStarting}
          className="text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
        >
          Cancel
        </Button>
        <Button
          color="blue"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={cn(
            'min-w-[160px] rounded-xl font-semibold',
            'shadow-lg shadow-blue-500/25 dark:shadow-blue-500/20',
            'transition-all duration-200',
            canSubmit &&
              'hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5',
          )}
        >
          {isStarting ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Creating...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Create Tasks
            </span>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
